import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { calculateShipping, validatePromoCode, calculateFlatDiscount } from '../../utils/pricing';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    payment_cod_enabled: '1',
    payment_cod_title: 'Cash on Delivery',
    payment_cod_description: 'Pay when you receive your order',
    payment_bank_transfer_enabled: '1',
    payment_bank_transfer_title: 'Direct Bank Transfer',
    payment_bank_transfer_description: 'Transfer payment directly to our bank account',
    payment_bank_name: '',
    payment_account_name: '',
    payment_account_number: '',
    payment_iban: '',
    payment_swift: '',
    payment_bank_address: '',
    payment_instructions: ''
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    paymentMethod: 'cod'
  });

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  useEffect(() => {
    loadCart();
    loadSettings();
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('appliedPromo');
    if (saved && cartItems.length > 0 && Object.keys(settings).length > 0) {
      try {
        const parsed = JSON.parse(saved);
        const result = validatePromoCode(parsed.code, subtotal, cartItems, settings);
        if (result.valid) {
          const applied = { code: parsed.code, discount: result.discount };
          setAppliedPromo(applied);
          localStorage.setItem('appliedPromo', JSON.stringify(applied));
        } else {
          setAppliedPromo(null);
          localStorage.removeItem('appliedPromo');
        }
      } catch (e) { }
    }
  }, [settings, cartItems]);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    if (cart.length === 0) {
      toast.info('Your cart is empty');
      navigate('/shop');
    }
    setCartItems(cart);
  };

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;
    const result = validatePromoCode(promoCode, subtotal, cartItems, settings);
    if (result.valid) {
      const applied = { code: promoCode.trim(), discount: result.discount };
      setAppliedPromo(applied);
      localStorage.setItem('appliedPromo', JSON.stringify(applied));
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    localStorage.removeItem('appliedPromo');
    setPromoCode('');
  };

  const getSubtotal = () => cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const getTotalItems = () => cartItems.reduce((total, item) => total + item.quantity, 0);

  const subtotal = getSubtotal();
  const shipping = calculateShipping(subtotal, formData.city, settings);
  const promoDiscount = appliedPromo?.discount || 0;
  const flatDiscount = calculateFlatDiscount(subtotal, settings);
  const discount = appliedPromo?.discount || 0;
  const total = Math.max(0, subtotal + shipping - discount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.address || !formData.whatsapp) {
      toast.error('Please fill all required fields, including WhatsApp number');
      setLoading(false);
      return;
    }

    // Strips any non-digit characters, then strips one or more leading zeros
    // (so "0300..." or "00300..." both become "300..."), then prepends the
    // country code. Falls back to +92 if countryCode is ever somehow missing,
    // so this can never produce "undefined" again like an older order did.
    const digitsOnly = formData.whatsapp.replace(/\D/g, '').replace(/^0+/, '');
    const fullPhoneNumber = (formData.countryCode || '+92') + digitsOnly;

    const orderData = {
      customer_name: `${formData.firstName} ${formData.lastName}`,
      customer_email: formData.email,
      customer_phone: fullPhoneNumber,
      customer_alt_phone: formData.altPhone ? formData.altPhone.trim() : null,
      address: formData.address,
      city: formData.city,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image
      })),
      subtotal_amount: subtotal,
      shipping_amount: shipping,
      promo_discount_amount: promoDiscount,
      flat_discount_amount: flatDiscount,
      total_amount: total,
      payment_method: formData.paymentMethod
    };

    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/orders.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      const data = await response.json();

      if (data.success) {
        localStorage.removeItem('cart');
        localStorage.removeItem('appliedPromo');
        toast.success('Order placed successfully!');
        navigate(`/order-confirmation?order=${data.order_number}`);
      } else {
        toast.error(data.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return null;
  }

  const isCodEnabled = settings.payment_cod_enabled === '1';
  const isBankTransferEnabled = settings.payment_bank_transfer_enabled === '1';

  const COUNTRY_CODES = [
    { code: '+92', label: '🇵🇰 +92 (Pakistan)' },
    { code: '+1', label: '🇺🇸 +1 (US/Canada)' },
    { code: '+44', label: '🇬🇧 +44 (UK)' },
    { code: '+971', label: '🇦🇪 +971 (UAE)' },
    { code: '+966', label: '🇸🇦 +966 (Saudi Arabia)' },
    { code: '+91', label: '🇮🇳 +91 (India)' },
  ];

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-breadcrumb">
          <span>Home</span> <span className="crumb-sep">&gt;</span> <span>Cart</span> <span className="crumb-sep">&gt;</span> <span className="crumb-current">Checkout</span>
        </div>

        <h1>Checkout</h1>
        <p className="checkout-subtitle">Enter your details and complete your order.</p>

        <div className="checkout-content">
          <div className="checkout-form-section">
            <form onSubmit={handleSubmit} className="checkout-form">

              <div className="checkout-card">
                <h2>Customer Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-row">                  
                  <div className="form-group">
                    <label>WhatsApp Number *</label>
                    <div className="phone-input-row">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="country-code-select"
                        required
                      >
                        {COUNTRY_CODES.map(c => (
                          <option key={c.code} value={c.code}>{c.label}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        placeholder="300 1234567"
                        required
                      />
                    </div>
                    <small className="ss-hint">Used to contact you about your order. Don't include the leading 0 — we'll remove it automatically if you do.</small>
                  </div>

                  <div className="form-group">
                    <label>Phone Number <span className="ss-optional">Optional</span></label>
                    <input
                      type="tel"
                      name="altPhone"
                      value={formData.altPhone}
                      onChange={handleChange}
                      placeholder="An alternate contact number, if different"
                    />
                  </div>
                </div>
              </div>

              <div className="checkout-card">
                <h2>Shipping Information</h2>
                <div className="form-group">
                  <label>Address *</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} required />
                </div>

                <div className="form-row form-row-three">
                  <div className="form-group">
                    <label>City</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Karachi" />
                    {settings.shipping_home_city && (
                      <small className="ss-hint" style={{ display: 'block', marginTop: 4 }}>
                        Orders in {settings.shipping_home_city} get our Home City rate.
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input type="text" name="state" value={formData.state} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} />
                  </div>
                </div>
              </div>

              <div className="checkout-card">
                <h2>Payment Method</h2>
                <div className="payment-methods">
                  {isCodEnabled && (
                    <label className={`payment-option ${formData.paymentMethod === 'cod' ? 'selected' : ''}`}>
                      <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleChange} />
                      <div className="payment-option-content">
                        <span className="payment-title">{settings.payment_cod_title || 'Cash on Delivery'}</span>
                        <span className="payment-description">{settings.payment_cod_description || 'Pay when you receive your order'}</span>
                      </div>
                    </label>
                  )}
                  {isBankTransferEnabled && (
                    <label className={`payment-option ${formData.paymentMethod === 'bank_transfer' ? 'selected' : ''}`}>
                      <input type="radio" name="paymentMethod" value="bank_transfer" checked={formData.paymentMethod === 'bank_transfer'} onChange={handleChange} />
                      <div className="payment-option-content">
                        <span className="payment-title">{settings.payment_bank_transfer_title || 'Direct Bank Transfer'}</span>
                        <span className="payment-description">{settings.payment_bank_transfer_description || 'Transfer payment directly to our bank account'}</span>
                      </div>
                    </label>
                  )}
                </div>

                {formData.paymentMethod === 'bank_transfer' && isBankTransferEnabled && (
                  <div className="bank-transfer-instructions">
                    <div className="security-notice">
                      <span className="security-icon">🔒</span>
                      <p><strong>Secure Payment</strong> - We do not store any payment information. Please use the bank details below to complete your transfer.</p>
                    </div>
                    <h3>Bank Transfer Details</h3>
                    <div className="bank-details-card">
                      {settings.payment_bank_name && <div className="bank-detail-row"><span className="detail-label">Bank Name:</span><span className="detail-value">{settings.payment_bank_name}</span></div>}
                      {settings.payment_account_name && <div className="bank-detail-row"><span className="detail-label">Account Name:</span><span className="detail-value">{settings.payment_account_name}</span></div>}
                      {settings.payment_account_number && <div className="bank-detail-row"><span className="detail-label">Account Number:</span><span className="detail-value">{settings.payment_account_number}</span></div>}
                      {settings.payment_iban && <div className="bank-detail-row"><span className="detail-label">IBAN:</span><span className="detail-value">{settings.payment_iban}</span></div>}
                      {settings.payment_swift && <div className="bank-detail-row"><span className="detail-label">SWIFT/BIC:</span><span className="detail-value">{settings.payment_swift}</span></div>}
                      {settings.payment_bank_address && <div className="bank-detail-row"><span className="detail-label">Bank Address:</span><span className="detail-value">{settings.payment_bank_address}</span></div>}
                    </div>
                    {settings.payment_instructions && (
                      <div className="payment-instructions-box">
                        <h4>Important Instructions:</h4>
                        <p>{settings.payment_instructions}</p>
                        <div className="instruction-note"><strong>⚠️ Note:</strong> Once payment is processed, kindly share the screenshot on our official whatsapp for confirmation.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="order-summary-section">
            <div className="order-summary">
              <h2>Order Summary</h2>

              <div className="summary-items">
                {cartItems.map(item => (
                  <div key={item.id} className="summary-item">
                    <div className="summary-item-thumb">
                      <img src={item.image || 'https://via.placeholder.com/60'} alt={item.name} />
                      <span className="summary-item-qty-badge">{item.quantity}</span>
                    </div>
                    <div className="item-info">
                      <span className="item-name">{item.name}</span>
                    </div>
                    <span className="item-price">PKR {(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              {appliedPromo ? (
                <div className="promo-row">
                  <span style={{ flex: 1, fontSize: '0.85rem' }}>Code <strong>{appliedPromo.code}</strong> applied</span>
                  <button type="button" onClick={handleRemovePromo}>Remove</button>
                </div>
              ) : (
                <div className="promo-row">
                  <input type="text" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} />
                  <button type="button" onClick={handleApplyPromo}>Apply</button>
                </div>
              )}

              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal ({getTotalItems()} items)</span>
                  <span>PKR {subtotal.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? 'Free' : `PKR ${shipping.toLocaleString()}`}</span>
                </div>
                {flatDiscount > 0 && (
                  <div className="summary-row">
                    <span>Flat Discount ({settings.flat_discount_percentage}%)</span>
                    <span>- PKR {flatDiscount.toLocaleString()}</span>
                  </div>
                )}
                {promoDiscount > 0 && (
                  <div className="summary-row">
                    <span>Promo Discount</span>
                    <span>- PKR {promoDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total</span>
                  <span>PKR {total.toLocaleString()}</span>
                </div>
              </div>

              <button type="submit" onClick={handleSubmit} disabled={loading} className="place-order-btn">
                {loading ? 'Placing Order...' : `Place Order - PKR ${total.toLocaleString()}`}
              </button>

              <div className="secure-checkout-badge">
                <span>🔒 Secure Checkout</span>
                <p>Your payment information is processed securely</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;